/**
 * useGymRequests Hook
 *
 * Custom hook for real-time subscription to the current user's gym requests.
 * Provides information about the status of their gym registration requests.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { GymRequest, GymRequestStatus } from '@flash/shared';

/**
 * Convert Firestore document to GymRequest type
 */
const docToGymRequest = (docId: string, docData: DocumentData): GymRequest => {
  return {
    id: docId,
    requesterId: docData['requesterId'] as string,
    status: docData['status'] as GymRequestStatus,
    name: docData['name'] as string,
    description: docData['description'] as string,
    address: docData['address'] as string,
    city: docData['city'] as string,
    state: docData['state'] as string,
    country: docData['country'] as string,
    adminNotes: (docData['adminNotes'] as string | null) ?? null,
    reviewedBy: (docData['reviewedBy'] as string | null) ?? null,
    reviewedAt: docData['reviewedAt'] ?? null,
    gymId: (docData['gymId'] as string | null) ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * State interface for gym requests query
 */
interface UseGymRequestsState {
  /** List of user's gym requests */
  requests: GymRequest[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGymRequests hook
 */
interface UseGymRequestsResult extends UseGymRequestsState {
  /** Refetch requests from Firestore */
  refetch: () => Promise<void>;
  /** Get requests by status */
  getRequestsByStatus: (status: GymRequestStatus) => GymRequest[];
  /** Check if user has any pending requests */
  hasPendingRequests: boolean;
}

/**
 * Hook to fetch and subscribe to the current user's gym requests
 *
 * @param options - Optional configuration
 * @param options.realtime - Whether to use real-time listener (default: true)
 * @returns Object containing requests, loading state, error, and utility functions
 *
 * @example
 * ```tsx
 * const { requests, loading, hasPendingRequests } = useGymRequests();
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <div>
 *     <p>You have {requests.length} gym requests</p>
 *     {hasPendingRequests && <p>Some requests are still pending review</p>}
 *   </div>
 * );
 * ```
 */
export const useGymRequests = (options: { realtime?: boolean } = {}): UseGymRequestsResult => {
  const { realtime = true } = options;
  const { user } = useAuth();

  const [state, setState] = useState<UseGymRequestsState>({
    requests: [],
    loading: true,
    error: null,
  });

  /**
   * Fetch requests from Firestore (one-time)
   */
  const fetchRequests = useCallback(async (): Promise<void> => {
    if (!user) {
      setState({ requests: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const requestsRef = collection(db, 'gymRequests');
      const requestsQuery = query(
        requestsRef,
        where('requesterId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(requestsQuery);

      const requests: GymRequest[] = snapshot.docs.map((docSnapshot) =>
        docToGymRequest(docSnapshot.id, docSnapshot.data())
      );

      setState({ requests, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym requests';
      console.error('Error fetching gym requests:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [user]);

  // Real-time subscription effect
  useEffect(() => {
    if (!user) {
      setState({ requests: [], loading: false, error: null });
      return;
    }

    // For non-realtime mode, just fetch once
    if (!realtime) {
      fetchRequests();
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const requestsRef = collection(db, 'gymRequests');
    const requestsQuery = query(
      requestsRef,
      where('requesterId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const requests: GymRequest[] = snapshot.docs.map((docSnapshot) =>
          docToGymRequest(docSnapshot.id, docSnapshot.data())
        );

        setState({ requests, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym requests';
        console.error('Error in gym requests subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, realtime, fetchRequests]);

  /**
   * Get requests filtered by status
   */
  const getRequestsByStatus = useCallback(
    (status: GymRequestStatus): GymRequest[] => {
      return state.requests.filter((r) => r.status === status);
    },
    [state.requests]
  );

  /**
   * Check if user has any pending requests
   */
  const hasPendingRequests = state.requests.some((r) => r.status === 'pending');

  return {
    ...state,
    refetch: fetchRequests,
    getRequestsByStatus,
    hasPendingRequests,
  };
};
